import React, {useCallback, useEffect, useMemo, useState} from "react";
import {ButtonToolbar, IconButton, Pagination, Table} from "rsuite";
import {FileDownload, PieChart} from "@rsuite/icons";
import GridToolbar from "./GridToolbar";
import useLocalStorage from "react-use-localstorage";

const style = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    backgroundColor: 'var(--rs-bg-card)',
    border: '1px solid var(--rs-border-secondary)',
};

const BasicGrid = (props) => {

    const {loading, data, columns, handleClick, exportTitle} = props;
    const [page, setPage] = useState(1);
    // local storage uses string for everything - must parseInt to use in code below
    const [limit, setLimit] = useLocalStorage('pageLimit', '10');

    const [searchFilter, setSearchFilter] = useState('');

    const [sortColumn, setSortColumn] = useState();
    const [sortType, setSortType] = useState();

    // if the passed in data changes, we need to reset to page 1
    // otherwise you see an empty grid
    useEffect(() => {
        setPage(1);
    }, [data]);

    // sorting
    const sortData = useCallback(() => {
        if (sortColumn && sortType) {
            if (data.length === 0) return data; // bad refresh state, do not attempt to sort on undefined
            if (typeof data[0][sortColumn] === 'string') {
                // compare the entire string
                // ignore case
                return data.sort((a,b) => {
                    let x = a[sortColumn];
                    let y = b[sortColumn];
                    // one off case for matchingContent coming from YoE queries
                    const numeric = x.includes('years') && x.includes('months');
                    if (sortType === 'asc') {
                        return x.localeCompare(y, undefined, { numeric: numeric, sensitivity: 'accent' });
                    } else {
                        return y.localeCompare(x, undefined, { numeric: numeric, sensitivity: 'accent' });
                    }
                });
            }
            return data.sort((a, b) => {
                let x = a[sortColumn];
                let y = b[sortColumn];
                if (sortType === 'asc') {
                    return x - y;
                } else {
                    return y - x;
                }
            });
        }
        return data;
    }, [data, sortColumn, sortType]);

    const handleSortColumn = (sortColumn, sortType) => {
        setTimeout(() => {
            setSortColumn(sortColumn);
            setSortType(sortType);
        }, 250);
    };

    // pagination
    const handleChangeLimit = useCallback((dataKey) => {
        setPage(1);
        setLimit(dataKey);
    }, [setLimit]);

    // if there is a search term, filter first by that, then page if necessary
    const pagedData = useMemo(()  => {
        return sortData()
            .filter((value) => Object.values(value).filter((v) => v?.toString().toLowerCase().includes(searchFilter)).length > 0)
            .filter((v, i) => {
                const intLimit = parseInt(limit);
                const start = intLimit * (page - 1);
                const end = start + intLimit;
                return i >= start && i < end;
            });
    }, [limit, sortData, page, searchFilter]);

    // search / filter callback for grid toolbar input
    const filterData = useCallback((value) => {
        setPage(1); // force user back to first page to avoid an empty grid on searching from another page
        setSearchFilter(value.toLowerCase()); // case insensitive search
    }, []);

    // access rowData for single click -> populate modal with data
    const CustomCell = ({dataKey, rowData, ...props}) => {
        return <Table.Cell {...props}
                           dataKey={dataKey}
                           onClick={() => handleClick(rowData)}
                           rowData={rowData}
        />;
    };

    const exportDataAsCsv = useCallback(() => {
        const columnKeys = columns.map((e) => e.key);
        const columnLabels = columns.map((e) => e.label);
        let csv = '';
        // TODO if total filtered data exceeds given page size, we will only export the first page
        //  this is an issue in other areas of the application as well, need to figure out the best approach.
        // if searchFilter is active, then we will return the subset data
        // otherwise return everything in the table
        const dataToExport = (searchFilter === '' ? data : pagedData);
        for(let row = 0; row < dataToExport.length; row++) {
            let keysAmount = columnLabels.length;
            let keysCounter = 0

            if (row === 0){
                for (let key of columnLabels){
                    csv += key.replace(',', '') + (keysCounter+1 < keysAmount ? ',' : '\r\n' )
                    keysCounter++
                }
                keysCounter = 0;
            }
            // format dates from ISO back to what is displayed in the grid
            for (let key of columnKeys){
                csv += ((key.search(/date$|Date$|dateEarned/) !== -1) ?
                    `${dataToExport[row][key]?.toLocaleString('en-us', { month: 'short' })} ${dataToExport[row][key]?.getFullYear()}` :
                    String(dataToExport[row][key]).replace(',', ''))
                    + (keysCounter+1 < keysAmount ? ',' : '\r\n' )
                keysCounter++
            }
        }

        const a = document.createElement("a");
        const file = new Blob([csv], {type: 'data:text/csv;charset=utf-8'});
        a.href = window.URL.createObjectURL(file);
        a.download = `${exportTitle}.csv`;
        a.click();
    }, [columns, data, exportTitle, pagedData, searchFilter]);

    // The backend deals with Dates as ISO 8601 strings.
    // The form will convert values to strings as well.
    // The underlying date library React Suite uses, only deals with Dates, not strings.
    // On initial fetch, any string date objects must be converted into Dates in order to be properly loaded into the grid.
    // So for any Cell that has a key that contains the word Date, we will convert it to a Date for display.
    // REST still only deals with strings.
    return useMemo(() => {
        return (
            <>
                <div style={style}>
                    <GridToolbar onSearchChange={filterData} />
                    <ButtonToolbar style={{justifyContent: 'end', marginRight: '1em'}}>
                        <IconButton
                            appearance="primary"
                            icon={ <FileDownload />}
                            disabled={data.length === 0 || pagedData.length === 0}
                            onClick={()=> exportDataAsCsv()}
                            title="Export Data to CSV"
                        />
                        <IconButton
                            appearance="primary"
                            icon={ <PieChart />}
                            disabled={true}
                            title="Graphing (coming soon)"
                        />
                    </ButtonToolbar>
                </div>
                <Table
                    loading={loading}
                    height={300}
                    hover
                    showHeader
                    autoHeight
                    data={pagedData}
                    sortColumn={sortColumn}
                    sortType={sortType}
                    onSortColumn={handleSortColumn}
                    bordered
                    cellBordered
                    headerHeight={40}
                    rowHeight={46}
                >
                    {columns.map(column => {
                        const {key, label, flexGrow, ...rest} = column;
                        return (
                            <Table.Column {...rest} key={key} flexGrow={flexGrow ? flexGrow : 1} fixed sortable>
                                <Table.HeaderCell>{label}</Table.HeaderCell>
                                {key.search(/date$|Date$|dateEarned/) === -1 ?
                                    <CustomCell {...rest} dataKey={key} /> :
                                    <CustomCell {...rest} dataKey={key}>
                                        {rowData => {
                                            // endDate could be present for exp/projects
                                            if (key === 'endDate' && rowData.isPresent === true) return 'Present';
                                            // any data add/edit, must send the date values back as Dates
                                            // otherwise this will blow up
                                            const rowDataAsDate = rowData[key];
                                            return `${rowDataAsDate?.toLocaleString('en-us', { month: 'short' })} ${rowDataAsDate?.getFullYear()}`;
                                        }}
                                    </CustomCell>
                                }
                            </Table.Column>
                        );
                    })}
                </Table>
                <Pagination
                    prev
                    next
                    first
                    last
                    ellipsis
                    boundaryLinks
                    maxButtons={5}
                    size="xs"
                    layout={['total', '-', 'limit', '|', 'pager', 'skip']}
                    total={data.length}
                    limitOptions={[10, 20, 50]}
                    limit={parseInt(limit)}
                    activePage={page}
                    onChangePage={setPage}
                    onChangeLimit={handleChangeLimit}
                    style={{padding: '0.5rem'}}
                    disabled={searchFilter !== ''}
                />
            </>
        );
        }, [columns, data, exportDataAsCsv, filterData, handleChangeLimit, limit, loading, page, pagedData, searchFilter, sortColumn, sortType]);
}

export default BasicGrid;